/**
 * Service for managing inference configurations.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface InferenceConfig {
  id: string;
  user_id: string;
  name: string;
  display_name?: string;
  description?: string;
  s3_base_path: string;
  project_id?: string | null;
  config?: any;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

class InferenceService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List all inference configurations for the current user.
   */
  async listInference(projectId?: string): Promise<InferenceConfig[]> {
    const headers = await this.getAuthHeaders();
    const url = projectId 
      ? `${BACKEND_URL}/api/storage/inference?project_id=${projectId}`
      : `${BACKEND_URL}/api/storage/inference`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list inference configurations');
    }

    const data = await response.json();
    console.log('[InferenceService] Response:', data);
    return data.inference || [];
  }

  /**
   * Get a specific inference configuration by ID.
   */
  async getInference(inferenceId: string): Promise<InferenceConfig> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/inference/${inferenceId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get inference configuration');
    }

    return response.json();
  }

  /**
   * Delete an inference configuration.
   */
  async deleteInference(inferenceId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/inference/${inferenceId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete inference configuration');
    }
  }
}

export const inferenceService = new InferenceService();
export default inferenceService;

